import type { FloorReason } from './effect-floor.ts'

export const BACKGROUND_EFFECTS = ['none', 'blur', 'image', 'custom_image'] as const

export type BackgroundEffect = (typeof BACKGROUND_EFFECTS)[number]

// The curated backgrounds the platform ships, by key. Ruby owns the list
// (TeleconsultationPreference::BACKGROUND_IMAGES); the web maps each key to its asset.
export const BACKGROUND_IMAGES = ['sala-clara', 'estudio', 'jardim', 'areia', 'noite'] as const

export type BackgroundImage = (typeof BACKGROUND_IMAGES)[number]

// One strength step is one sigma of the processor's quarter-resolution blur pass, so every
// step the slider offers changes the picture; a finer scale would have steps that do nothing.
export const BACKGROUND_BLUR_STRENGTH = { min: 1, max: 10, default: 3 } as const
const BLUR_RADIUS_PER_STRENGTH = 4

export function parseBackgroundEffect(value: unknown): BackgroundEffect {
  return typeof value === 'string' && (BACKGROUND_EFFECTS as readonly string[]).includes(value)
    ? (value as BackgroundEffect)
    : 'none'
}

export function parseBackgroundImage(value: unknown): BackgroundImage | null {
  return typeof value === 'string' && (BACKGROUND_IMAGES as readonly string[]).includes(value)
    ? (value as BackgroundImage)
    : null
}

// A same-origin path is the whole trust boundary for a URL that reaches an <img> feeding a
// WebGL texture, so the check cannot rely on `startsWith`/`URL` alone: a browser folds a
// backslash into a forward slash and strips embedded tab/CR/LF before parsing a URL, so
// "/\\evil.com/x" and "/\r\n//evil.com" both resolve off-origin despite starting with one
// slash. No backslash, no whitespace and no control character may appear anywhere in the path.
const SAME_ORIGIN_PATH = /^\/(?![/\\])[^\s\\\p{Cc}]*$/u

export function parseSameOriginPath(value: unknown): string | null {
  return typeof value === 'string' && SAME_ORIGIN_PATH.test(value) ? value : null
}

export function parseBackgroundBlurStrength(value: unknown): number {
  if (typeof value !== 'number' || !Number.isInteger(value)) return BACKGROUND_BLUR_STRENGTH.default
  return Math.min(BACKGROUND_BLUR_STRENGTH.max, Math.max(BACKGROUND_BLUR_STRENGTH.min, value))
}

export function blurRadiusFor(strength: number): number {
  return parseBackgroundBlurStrength(strength) * BLUR_RADIUS_PER_STRENGTH
}

export function wantsProcessing(effect: BackgroundEffect): boolean {
  return effect !== 'none'
}

export const BACKGROUND_MASK = {
  maskThreshold: 0.7,
  maskHysteresis: 0.05,
  maskFeather: 0.25,
  temporalSmoothing: 0.6,
  guideRadius: 3,
  guideRange: 0.26,
  motionRange: 0.26,
  edgeBlurRadius: 8,
}

export type MaskOptions = { [K in keyof typeof BACKGROUND_MASK]: number }
export type MaskKnob = keyof MaskOptions

export const MASK_KNOBS: Record<MaskKnob, { min: number; max: number; step: number }> = {
  maskThreshold: { min: 0.2, max: 0.8, step: 0.01 },
  maskHysteresis: { min: 0, max: 0.2, step: 0.01 },
  maskFeather: { min: 0.01, max: 0.3, step: 0.01 },
  temporalSmoothing: { min: 0, max: 0.9, step: 0.05 },
  guideRadius: { min: 0, max: 4, step: 0.5 },
  guideRange: { min: 0.02, max: 0.5, step: 0.01 },
  motionRange: { min: 0.02, max: 0.5, step: 0.01 },
  edgeBlurRadius: { min: 0, max: 32, step: 2 },
}

export const SEGMENTER_VIEWS = ['normal', 'confidence', 'alpha'] as const
export type SegmenterView = (typeof SEGMENTER_VIEWS)[number]

export function isSegmenterView(value: unknown): value is SegmenterView {
  return (SEGMENTER_VIEWS as readonly unknown[]).includes(value)
}

export function debugViewFor(view: SegmenterView): number {
  return SEGMENTER_VIEWS.indexOf(view)
}

export function parseMaskOptions(value: unknown): MaskOptions {
  const source: Partial<Record<MaskKnob, unknown>> =
    typeof value === 'object' && value !== null ? value : {}
  const parsed = { ...BACKGROUND_MASK }
  for (const knob of Object.keys(MASK_KNOBS) as MaskKnob[]) {
    const candidate = source[knob]
    const { min, max } = MASK_KNOBS[knob]
    parsed[knob] =
      typeof candidate === 'number' && Number.isFinite(candidate)
        ? Math.min(max, Math.max(min, candidate))
        : BACKGROUND_MASK[knob]
  }
  return parsed
}

export type ProcessorChoice =
  | { mode: 'disabled' }
  | { mode: 'background-blur'; blurRadius: number }
  | { mode: 'virtual-background'; imagePath: string }

// The same shape the processor's `switchTo` takes, so a live instance moves between blur and
// image without being rebuilt. An image effect with no resolvable path is treated as no
// effect: the alternative is a passthrough instance that still segments every frame.
export function resolveProcessorChoice(
  effect: BackgroundEffect,
  strength: number,
  imagePath: string | null,
): ProcessorChoice {
  if (effect === 'blur') return { mode: 'background-blur', blurRadius: blurRadiusFor(strength) }
  if ((effect === 'image' || effect === 'custom_image') && imagePath) {
    return { mode: 'virtual-background', imagePath }
  }
  return { mode: 'disabled' }
}

export type CameraOffReason = 'effect_unavailable' | 'device_too_slow'

export interface JoinCameraDecision {
  videoEnabled: boolean
  cameraOffReason: CameraOffReason | null
}

export function resolveJoinCamera(input: {
  effect: BackgroundEffect
  effectAvailable: boolean
  videoEnabled: boolean
  floor?: FloorReason | null
}): JoinCameraDecision {
  if (!input.videoEnabled) return { videoEnabled: false, cameraOffReason: null }
  if (wantsProcessing(input.effect)) {
    if (input.floor === 'device_too_slow') {
      return { videoEnabled: false, cameraOffReason: 'device_too_slow' }
    }
    if (!input.effectAvailable || input.floor) {
      return { videoEnabled: false, cameraOffReason: 'effect_unavailable' }
    }
  }
  return { videoEnabled: true, cameraOffReason: null }
}
